import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { SITE, waLink } from "@/data/site";

const LINKS = [
  { label: "Koleksiyonlar", href: "#katalog" },
  { label: "Çok Satanlar", href: "#cok-satanlar" },
  { label: "Seçki", href: "#secki" },
  { label: "Hikâyemiz", page: "/hikayemiz" },
  { label: "Kategoriler", href: "#kategoriler" },
  { label: "Teknik Servis", page: "/teknik-servis" },
  { label: "İletişim", href: "#iletisim" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    const delay = open ? 350 : 0;
    if (l.page) {
      setTimeout(() => {
        navigate(l.page);
        window.scrollTo(0, 0);
      }, delay);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(l.href), 500);
    } else {
      setTimeout(() => scrollToId(l.href), delay);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    window.scrollTo(0, 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-xl border-b border-emerald/10 shadow-sm"
            : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={goHome}
            className="flex items-baseline gap-2 group"
            data-testid="nav-logo"
          >
            <span className={`font-serif text-xl sm:text-2xl font-semibold tracking-tight transition-colors duration-500 ${scrolled ? "text-ink" : "text-ivory"}`}>
              Atılmaz <span className="italic text-gold">Saat</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-links">
            {LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l)}
                data-testid={`nav-link-${(l.page || l.href).replace(/^[#/]+/, "").replace(/\//g, "-")}`}
                className={`text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  scrolled ? "text-ink hover:text-emerald" : "text-ivory/90 hover:text-ivory"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-cta"
              className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 h-10 text-[13px] font-semibold transition-colors duration-300 ${
                scrolled
                  ? "bg-emerald text-ivory hover:bg-emerald-deep"
                  : "bg-ivory text-emerald hover:bg-sand"
              }`}
            >
              <MessageCircle size={15} strokeWidth={2.2} />
              WhatsApp Destek
            </a>
            <button
              onClick={() => setOpen(true)}
              aria-label="Menüyü aç"
              data-testid="nav-mobile-menu-button"
              className={`lg:hidden h-11 w-11 inline-flex items-center justify-center transition-colors ${scrolled ? "text-ink" : "text-ivory"}`}
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-emerald-deep flex flex-col"
            data-testid="nav-mobile-drawer"
          >
            <div className="h-16 px-4 flex items-center justify-between">
              <span className="font-serif text-xl font-semibold text-ivory">
                Atılmaz <span className="italic text-gold">Saat</span>
              </span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Menüyü kapat"
                data-testid="nav-mobile-close-button"
                className="h-11 w-11 inline-flex items-center justify-center text-ivory"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <div key={l.label} className="overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.08 + i * 0.06 }}
                    onClick={() => go(l)}
                    data-testid={`nav-mobile-link-${(l.page || l.href).replace(/^[#/]+/, "").replace(/\//g, "-")}`}
                    className="font-serif text-4xl font-medium text-ivory py-2 hover:text-gold transition-colors text-left"
                  >
                    {l.label}
                  </motion.button>
                </div>
              ))}
              <motion.a
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp-cta"
                className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-ivory px-7 h-12 text-sm font-semibold text-emerald"
              >
                <MessageCircle size={16} />
                WhatsApp Destek
              </motion.a>
            </nav>
            <p className="px-8 pb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory/40">
              {SITE.slogan}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
